import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User } from '../../services/authService';
import { financeAssetsService, FinanceAsset, FinanceAssetFormData } from '../../services/financeAssetsService';
import './FinanceAssets.css';

interface FinanceAssetsProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

const ASSET_CATEGORIES = [
  { value: 'stocks', label: 'Stocks', icon: '📈', type: 'asset' },
  { value: 'bonds', label: 'Bonds', icon: '📋', type: 'asset' },
  { value: 'real_estate', label: 'Real Estate', icon: '🏠', type: 'asset' },
  { value: 'commodities', label: 'Commodities', icon: '🥇', type: 'asset' },
  { value: 'gold', label: 'Gold', icon: '🥇', type: 'asset' },
  { value: 'crypto', label: 'Cryptocurrency', icon: '₿', type: 'asset' },
  { value: 'cash', label: 'Cash', icon: '💵', type: 'asset' },
  { value: 'retirement', label: 'Retirement', icon: '🏦', type: 'asset' },
  { value: 'insurance', label: 'Insurance', icon: '🛡️', type: 'asset' },
  { value: 'collectibles', label: 'Collectibles', icon: '🎨', type: 'asset' },
  { value: 'business', label: 'Business', icon: '🏢', type: 'asset' },
  { value: 'other', label: 'Other Assets', icon: '📦', type: 'asset' },
  // Debt categories
  { value: 'mortgage', label: 'Mortgage', icon: '🏠', type: 'debt' },
  { value: 'credit_card', label: 'Credit Card', icon: '💳', type: 'debt' },
  { value: 'personal_loan', label: 'Personal Loan', icon: '💰', type: 'debt' },
  { value: 'student_loan', label: 'Student Loan', icon: '🎓', type: 'debt' },
  { value: 'auto_loan', label: 'Auto Loan', icon: '🚗', type: 'debt' },
  { value: 'business_loan', label: 'Business Loan', icon: '🏢', type: 'debt' },
  { value: 'other_debt', label: 'Other Debt', icon: '📋', type: 'debt' }
] as const;

const CATEGORY_COLORS = [
  // Asset colors (blues and greens)
  '#2E7D32', '#388E3C', '#43A047', '#4CAF50', '#66BB6A',
  '#1976D2', '#1E88E5', '#2196F3', '#42A5F5', '#64B5F6',
  '#455A64', '#546E7A',
  // Debt colors (reds and oranges)
  '#C62828', '#D32F2F', '#E53935', '#F44336', '#EF5350',
  '#E65100', '#F57C00'
];

const DEBT_COLORS = [
  '#C62828', '#D32F2F', '#E53935', '#F44336', '#EF5350',
  '#E65100', '#F57C00'
];

const FinanceAssets: React.FC<FinanceAssetsProps> = ({ user, onClose, onToggleSidebar }) => {
  const navigate = useNavigate();
  const [assets, setAssets] = useState<FinanceAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [viewType, setViewType] = useState<'all' | 'assets' | 'debts'>('all');
  const [activeTab, setActiveTab] = useState<'overview' | 'assets' | 'debts' | 'add'>('overview');
  const [newAsset, setNewAsset] = useState<FinanceAssetFormData>({
    name: '',
    notes: '',
    category: 'other',
    sub_category: '',
    value: 0,
    valuation_date: financeAssetsService.getCurrentDate(),
    valuation_month: financeAssetsService.getCurrentMonth()
  });

  useEffect(() => {
    if (user?.id) {
      loadAssets();
      // Test NaN handling in development
      if (process.env.NODE_ENV === 'development') {
        financeAssetsService.testNaNHandling();
      }
    }
  }, [user, selectedCategory]);

  const loadAssets = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      setError(null);

      const filters = {
        user_id: user.id.toString(),
        category: selectedCategory ? selectedCategory as any : undefined,
        limit: 100
      };

      const response = await financeAssetsService.getUserFinanceAssets(filters);

      if (response.success && response.assets) {
        setAssets(response.assets);
      } else {
        setError('Failed to load assets');
      }
    } catch (err) {
      console.error('Error loading assets:', err);
      setError('Failed to load assets');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAsset = async () => {
    if (!user?.id || !newAsset.name.trim() || newAsset.value <= 0) return;

    try {
      const validation = financeAssetsService.validateAssetData(newAsset);
      if (!validation.isValid) {
        setError(validation.errors.join(', '));
        return;
      }

      const response = await financeAssetsService.createFinanceAsset(user.id.toString(), newAsset);
      
      if (response.success) {
        setNewAsset({
          name: '',
          notes: '',
          category: 'other',
          sub_category: '',
          value: 0,
          valuation_date: financeAssetsService.getCurrentDate(),
          valuation_month: financeAssetsService.getCurrentMonth()
        });
        setShowAddForm(false);
        setActiveTab('overview'); // Switch back to overview after adding
        await loadAssets();
      } else {
        setError(response.message || 'Failed to create asset');
      }
    } catch (err) {
      console.error('Error creating asset:', err);
      setError('Failed to create asset');
    }
  };

  const handleDeleteAsset = async (assetId: number) => {
    if (!window.confirm('Are you sure you want to delete this asset?')) return;

    try {
      const response = await financeAssetsService.deleteFinanceAsset(assetId.toString());
      
      if (response.success) {
        await loadAssets();
      } else {
        setError(response.message || 'Failed to delete asset');
      }
    } catch (err) {
      console.error('Error deleting asset:', err);
      setError('Failed to delete asset');
    }
  };

  const getCategoryIcon = (category: string) => {
    const categoryInfo = ASSET_CATEGORIES.find(cat => cat.value === category);
    return categoryInfo?.icon || '📦';
  };

  const getCategoryLabel = (category: string) => {
    const categoryInfo = ASSET_CATEGORIES.find(cat => cat.value === category);
    return categoryInfo?.label || 'Other';
  };

  const getCategoryType = (category: string): 'asset' | 'debt' => {
    const categoryInfo = ASSET_CATEGORIES.find(cat => cat.value === category);
    return categoryInfo?.type || 'asset';
  };

  const isDebtCategory = (category: string): boolean => {
    return getCategoryType(category) === 'debt';
  };

  const calculatePortfolioData = () => {
    const assetTotals: { [key: string]: number } = {};
    const debtTotals: { [key: string]: number } = {};
    let totalAssets = 0;
    let totalDebts = 0;

    assets.forEach(asset => {
      // Ensure asset.value is a valid number
      let value = 0;
      if (asset.value !== null && asset.value !== undefined) {
        const parsedValue = typeof asset.value === 'number' ? asset.value : parseFloat(String(asset.value));
        value = isNaN(parsedValue) || !isFinite(parsedValue) ? 0 : Math.abs(parsedValue);
      }

      if (isDebtCategory(asset.category)) {
        debtTotals[asset.category] = (debtTotals[asset.category] || 0) + value;
        totalDebts += value;
      } else {
        assetTotals[asset.category] = (assetTotals[asset.category] || 0) + value;
        totalAssets += value;
      }
    });

    const netWorth = totalAssets - totalDebts;

    // Create portfolio data for visualization
    const assetData = Object.entries(assetTotals).map(([category, value], index) => ({
      category,
      value: isNaN(value) || !isFinite(value) ? 0 : value,
      percentage: totalAssets > 0 && isFinite(totalAssets) ? ((isNaN(value) || !isFinite(value) ? 0 : value) / totalAssets) * 100 : 0,
      color: CATEGORY_COLORS[index % CATEGORY_COLORS.length],
      type: 'asset' as const
    }));

    const debtData = Object.entries(debtTotals).map(([category, value], index) => ({
      category,
      value: isNaN(value) || !isFinite(value) ? 0 : value,
      percentage: totalDebts > 0 && isFinite(totalDebts) ? ((isNaN(value) || !isFinite(value) ? 0 : value) / totalDebts) * 100 : 0,
      color: DEBT_COLORS[index % DEBT_COLORS.length],
      type: 'debt' as const
    }));

    return { 
      assetData, 
      debtData, 
      totalAssets: isNaN(totalAssets) || !isFinite(totalAssets) ? 0 : totalAssets, 
      totalDebts: isNaN(totalDebts) || !isFinite(totalDebts) ? 0 : totalDebts, 
      netWorth: isNaN(netWorth) || !isFinite(netWorth) ? 0 : netWorth,
      portfolioData: [...assetData, ...debtData]
    };
  };

  const renderPieChart = () => {
    const { assetData, debtData, totalAssets, totalDebts, netWorth } = calculatePortfolioData();
    
    if (assetData.length === 0 && debtData.length === 0) return null;

    return (
      <div className="fa-portfolio-overview">
        {/* Net Worth Summary */}
        <div className="fa-net-worth-summary">
          <div className="fa-summary-cards">
            <div className="fa-summary-card fa-assets">
              <div className="fa-summary-icon">📈</div>
              <div className="fa-summary-content">
                <div className="fa-summary-label">Total Assets</div>
                <div className="fa-summary-value">{financeAssetsService.formatCurrency(totalAssets, 'VND')}</div>
              </div>
            </div>
            
            <div className="fa-summary-card fa-debts">
              <div className="fa-summary-icon">📉</div>
              <div className="fa-summary-content">
                <div className="fa-summary-label">Total Debts</div>
                <div className="fa-summary-value">{financeAssetsService.formatCurrency(totalDebts, 'VND')}</div>
              </div>
            </div>
            
            <div className={`fa-summary-card fa-net-worth ${netWorth >= 0 ? 'positive' : 'negative'}`}>
              <div className="fa-summary-icon">{netWorth >= 0 ? '💰' : '⚠️'}</div>
              <div className="fa-summary-content">
                <div className="fa-summary-label">Net Worth</div>
                <div className="fa-summary-value">{financeAssetsService.formatCurrency(netWorth, 'VND')}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Assets and Debts Breakdown */}
        <div className="fa-breakdown-section">
          {assetData.length > 0 && (
            <div className="fa-breakdown-column">
              <h3 className="fa-breakdown-title">📈 Assets</h3>
              <div className="fa-breakdown-list">
                {assetData.map((segment) => (
                  <div key={segment.category} className="fa-legend-item">
                    <div 
                      className="fa-legend-color" 
                      style={{ backgroundColor: segment.color }}
                    ></div>
                    <div className="fa-legend-info">
                      <span className="fa-legend-category">
                        {getCategoryIcon(segment.category)} {getCategoryLabel(segment.category)}
                      </span>
                      <span className="fa-legend-value">
                        {financeAssetsService.formatCurrency(segment.value, 'VND')} 
                        <span className="fa-legend-percentage">
                          ({isNaN(segment.percentage) || !isFinite(segment.percentage) ? '0.0' : segment.percentage.toFixed(1)}%)
                        </span>
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {debtData.length > 0 && (
            <div className="fa-breakdown-column">
              <h3 className="fa-breakdown-title">📉 Debts</h3>
              <div className="fa-breakdown-list">
                {debtData.map((segment) => (
                  <div key={segment.category} className="fa-legend-item">
                    <div 
                      className="fa-legend-color" 
                      style={{ backgroundColor: segment.color }}
                    ></div>
                    <div className="fa-legend-info">
                      <span className="fa-legend-category">
                        {getCategoryIcon(segment.category)} {getCategoryLabel(segment.category)}
                      </span>
                      <span className="fa-legend-value">
                        {financeAssetsService.formatCurrency(segment.value, 'VND')} 
                        <span className="fa-legend-percentage">
                          ({isNaN(segment.percentage) || !isFinite(segment.percentage) ? '0.0' : segment.percentage.toFixed(1)}%)
                        </span>
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (!user) {
    return (
      <div className="finance-assets">
        <div className="fa-header">
          <button className="sidebar-toggle" onClick={onToggleSidebar}>☰</button>
          <h1>💰 Financial Overview</h1>
        </div>
        <div className="fa-auth-required">
          <p>Please sign in to manage your financial assets</p>
        </div>
      </div>
    );
  }

    return (
      <div className="finance-assets">
        <div className="fa-header">
          <button className="sidebar-toggle" onClick={onToggleSidebar}>☰</button>
          <h1>💰 Financial Overview</h1>
          <button 
            className="fa-add-asset-btn"
            onClick={() => setActiveTab('add')}
            title="Add new asset"
          >
            ➕
          </button>
      </div>

      {error && (
        <div className="fa-error-message">
          <span>{error}</span>
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

        <div className="fa-main-content">
          {activeTab === 'overview' && (
            <div className="fa-tab-content">
              {assets.length > 0 && renderPieChart()}
              {assets.length === 0 && (
                <div className="fa-empty-state">
                  <p>No assets found</p>
                  <button onClick={() => setActiveTab('add')} className="fa-add-first-asset-btn">
                    Add your first asset
                  </button>
                </div>
              )}
            </div>
          )}

          {activeTab === 'assets' && (
            <div className="fa-tab-content">
              <div className="fa-filters">
                <button 
                  className={`fa-filter-btn ${selectedCategory === '' ? 'active' : ''}`}
                  onClick={() => setSelectedCategory('')}
                >
                  All Asset Categories
                </button>
                {ASSET_CATEGORIES
                  .filter(category => category.type === 'asset')
                  .map(category => {
                    const categoryAssets = assets.filter(asset => asset.category === category.value);
                    if (categoryAssets.length === 0) return null;
                    
                    return (
                      <button 
                        key={category.value}
                        className={`fa-filter-btn ${selectedCategory === category.value ? 'active' : ''}`}
                        onClick={() => setSelectedCategory(category.value)}
                      >
                        {category.icon} {category.label} ({categoryAssets.length})
                      </button>
                    );
                  })}
              </div>

              <div className="fa-asset-list-container">
                <div className="fa-asset-list">
                  {loading ? (
                    <div className="fa-loading-state">
                      <div className="fa-loading-spinner"></div>
                      <p>Loading assets...</p>
                    </div>
                  ) : assets.filter(asset => !isDebtCategory(asset.category)).length === 0 ? (
                    <div className="fa-empty-state">
                      <p>No assets found</p>
                      <button onClick={() => setActiveTab('add')} className="fa-add-first-asset-btn">
                        Add your first asset
                      </button>
                    </div>
                  ) : (
                    assets
                      .filter(asset => !isDebtCategory(asset.category))
                      .map((asset) => (
                        <div 
                          key={asset.id} 
                          className="fa-asset-item asset-item"
                          onClick={() => navigate(`/finance/assets/${asset.id}`)}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="fa-asset-main">
                            <div className="fa-asset-icon asset-icon">
                              {getCategoryIcon(asset.category)}
                            </div>
                            <div className="fa-asset-content">
                              <h3 className="fa-asset-name">{asset.name}</h3>
                              {asset.sub_category && (
                                <p className="fa-asset-subcategory">{asset.sub_category}</p>
                              )}
                              {asset.notes && (
                                <p className="fa-asset-notes">{asset.notes}</p>
                              )}
                              <div className="fa-asset-meta">
                                <span className="fa-asset-category">
                                  {getCategoryLabel(asset.category)}
                                </span>
                                <span className="fa-asset-date">
                                  Updated: {new Date(asset.valuation_date).toLocaleDateString()}
                                </span>
                              </div>
                            </div>
                            <div className="fa-asset-value asset-value">
                              {(() => {
                                const value = asset.value;
                                if (value === null || value === undefined || isNaN(value) || !isFinite(value)) {
                                  return financeAssetsService.formatCurrency(0, 'VND');
                                }
                                return financeAssetsService.formatCurrency(value, 'VND');
                              })()}
                            </div>
                          </div>
                          <div className="fa-asset-actions">
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteAsset(asset.id);
                              }}
                              className="fa-delete-btn"
                              title="Delete asset"
                            >
                              🗑️
                            </button>
                          </div>
                        </div>
                      ))
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'debts' && (
            <div className="fa-tab-content">
              <div className="fa-filters">
                <button 
                  className={`fa-filter-btn ${selectedCategory === '' ? 'active' : ''}`}
                  onClick={() => setSelectedCategory('')}
                >
                  All Debt Categories
                </button>
                {ASSET_CATEGORIES
                  .filter(category => category.type === 'debt')
                  .map(category => {
                    const categoryAssets = assets.filter(asset => asset.category === category.value);
                    if (categoryAssets.length === 0) return null;
                    
                    return (
                      <button 
                        key={category.value}
                        className={`fa-filter-btn ${selectedCategory === category.value ? 'active' : ''}`}
                        onClick={() => setSelectedCategory(category.value)}
                      >
                        {category.icon} {category.label} ({categoryAssets.length})
                      </button>
                    );
                  })}
              </div>

              <div className="fa-asset-list-container">
                <div className="fa-asset-list">
                  {loading ? (
                    <div className="fa-loading-state">
                      <div className="fa-loading-spinner"></div>
                      <p>Loading debts...</p>
                    </div>
                  ) : assets.filter(asset => isDebtCategory(asset.category)).length === 0 ? (
                    <div className="fa-empty-state">
                      <p>No debts found</p>
                      <button onClick={() => setActiveTab('add')} className="fa-add-first-asset-btn">
                        Add your first debt
                      </button>
                    </div>
                  ) : (
                    assets
                      .filter(asset => isDebtCategory(asset.category))
                      .map((asset) => (
                        <div 
                          key={asset.id} 
                          className="fa-asset-item debt-item"
                          onClick={() => navigate(`/finance/assets/${asset.id}`)}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="fa-asset-main">
                            <div className="fa-asset-icon debt-icon">
                              {getCategoryIcon(asset.category)}
                            </div>
                            <div className="fa-asset-content">
                              <h3 className="fa-asset-name">
                                {asset.name}
                                <span className="fa-debt-badge">DEBT</span>
                              </h3>
                              {asset.sub_category && (
                                <p className="fa-asset-subcategory">{asset.sub_category}</p>
                              )}
                              {asset.notes && (
                                <p className="fa-asset-notes">{asset.notes}</p>
                              )}
                              <div className="fa-asset-meta">
                                <span className="fa-asset-category">
                                  {getCategoryLabel(asset.category)}
                                </span>
                                <span className="fa-asset-date">
                                  Updated: {new Date(asset.valuation_date).toLocaleDateString()}
                                </span>
                              </div>
                            </div>
                            <div className="fa-asset-value debt-value">
                              -{(() => {
                                const value = asset.value;
                                if (value === null || value === undefined || isNaN(value) || !isFinite(value)) {
                                  return financeAssetsService.formatCurrency(0, 'VND');
                                }
                                return financeAssetsService.formatCurrency(value, 'VND');
                              })()}
                            </div>
                          </div>
                          <div className="fa-asset-actions">
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteAsset(asset.id);
                              }}
                              className="fa-delete-btn"
                              title="Delete debt"
                            >
                              🗑️
                            </button>
                          </div>
                        </div>
                      ))
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'add' && (
            <div className="fa-tab-content">
        <div className="fa-add-asset-form">
          <div className="fa-form-row">
            <input
              type="text"
              placeholder="Asset name..."
              value={newAsset.name}
              onChange={(e) => setNewAsset({ ...newAsset, name: e.target.value })}
              className="fa-asset-name-input"
            />
            <input
              type="number"
              placeholder="Value"
              value={newAsset.value || ''}
              onChange={(e) => {
                const inputValue = e.target.value;
                let numValue = 0;
                
                if (inputValue !== '') {
                  const parsed = parseFloat(inputValue);
                  numValue = isNaN(parsed) || !isFinite(parsed) ? 0 : Math.abs(parsed);
                }
                
                setNewAsset({ ...newAsset, value: numValue });
              }}
              className="fa-asset-value-input"
              min="0"
              step="0.01"
            />
          </div>
          
          <div className="fa-form-row">
            <select
              value={newAsset.category}
              onChange={(e) => setNewAsset({ ...newAsset, category: e.target.value as any })}
              className="fa-category-select"
            >
              {ASSET_CATEGORIES.map(category => (
                <option key={category.value} value={category.value}>
                  {category.icon} {category.label}
                </option>
              ))}
            </select>
            <input
              type="text"
              placeholder="Sub-category (optional)"
              value={newAsset.sub_category}
              onChange={(e) => setNewAsset({ ...newAsset, sub_category: e.target.value })}
              className="fa-subcategory-input"
            />
          </div>

          <textarea
            placeholder="Notes (optional)..."
            value={newAsset.notes}
            onChange={(e) => setNewAsset({ ...newAsset, notes: e.target.value })}
            className="fa-notes-input"
            rows={2}
          />

          <div className="fa-form-actions">
            <button 
              onClick={handleAddAsset} 
              className="fa-save-asset-btn" 
              disabled={!newAsset.name.trim() || newAsset.value <= 0}
            >
              Save Asset
            </button>
                  <button onClick={() => {
                    setNewAsset({
                      name: '',
                      notes: '',
                      category: 'other',
                      sub_category: '',
                      value: 0,
                      valuation_date: financeAssetsService.getCurrentDate(),
                      valuation_month: financeAssetsService.getCurrentMonth()
                    });
                  }} className="fa-cancel-btn">
                    Clear Form
            </button>
                </div>
          </div>
        </div>
      )}
        </div>

        {/* Bottom Tab Navigation */}
        <div className="fa-bottom-tabs">
        <button 
            className={`fa-tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
        >
            <span className="fa-tab-icon">📊</span>
            <span className="fa-tab-label">Overview</span>
        </button>
        <button 
            className={`fa-tab-btn ${activeTab === 'assets' ? 'active' : ''}`}
            onClick={() => setActiveTab('assets')}
        >
            <span className="fa-tab-icon">📈</span>
            <span className="fa-tab-label">Assets</span>
        </button>
        <button 
            className={`fa-tab-btn ${activeTab === 'debts' ? 'active' : ''}`}
            onClick={() => setActiveTab('debts')}
        >
            <span className="fa-tab-icon">📉</span>
            <span className="fa-tab-label">Debts</span>
        </button>
      </div>
    </div>
  );
};

export default FinanceAssets;
